import { useEffect, useState, useRef } from 'react';

// Live horizontal ticker -- pulls /api/ticker every 60s and renders items
// as a continuous marquee. Pause on hover. Each item is clickable; clicks
// fan out via the onAction prop so the parent App can route to the right
// detail card / navigation behavior.

const API = import.meta.env.VITE_API_BASE || '/api';
const REFRESH_MS = 60 * 1000;

// Severity color palette -- aligned with the warm token system
const SEV = {
  critical:  { dot: 'var(--red)', cat: 'var(--red)', glow: 'rgba(224,90,90,0.5)' },
  warning:   { dot: 'var(--warn)', cat: 'var(--warn)', glow: 'rgba(212,99,74,0.3)' },
  info:      { dot: 'var(--cyan)', cat: 'var(--cyan)', glow: 'transparent' },
  highlight: { dot: 'var(--accent)', cat: 'var(--accent)', glow: 'rgba(233,179,93,0.3)' },
};

const MONO = 'var(--mono)';

export default function Ticker({ observer, onAction, height = 30, compact = false }) {
  const [items, setItems] = useState([]);
  const [paused, setPaused] = useState(false);

  // Fetch items, refresh every minute
  useEffect(() => {
    let cancelled = false;
    let timer;
    async function tick() {
      try {
        const q = (observer?.lat != null && observer?.lon != null)
          ? `?lat=${observer.lat}&lon=${observer.lon}`
          : '';
        const r = await fetch(`${API}/ticker${q}`);
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setItems(d.items || []);
      } catch { /* retain previous items */ }
    }
    tick();
    timer = setInterval(tick, REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [observer?.lat, observer?.lon]);

  if (!items.length) {
    return (
      <div style={{
        height, background: 'var(--bg)',
        borderBottom: '1px solid var(--hair)',
        display: 'flex', alignItems: 'center', padding: '0 16px',
        fontFamily: MONO, fontSize: 11, color: 'var(--txt-dim)',
        letterSpacing: '0.05em',
      }}>Loading sky data…</div>
    );
  }

  // Reduced motion: no marquee, render ONE copy of the items in a manually
  // scrollable row instead of an auto-scrolling loop (spec §6 / WCAG 2.3.3).
  const reduceMotion = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  // Double the items so the marquee loop is seamless
  const doubled = reduceMotion ? items : [...items, ...items];

  return (
    <div style={{
      height,
      background: 'var(--bg)',
      borderBottom: '1px solid var(--hair)',
      overflow: 'hidden', position: 'relative', zIndex: 49,
    }}>
      <div
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
        style={{
          display: 'flex', alignItems: 'center',
          whiteSpace: 'nowrap', height: '100%',
          ...(reduceMotion
            ? { overflowX: 'auto', WebkitOverflowScrolling: 'touch' }
            : {
                animation: `tickerScroll ${compact ? Math.max(90, items.length * 12) : Math.max(60, items.length * 8)}s linear infinite`,
                animationPlayState: paused ? 'paused' : 'running',
              }),
        }}
      >
        {doubled.map((item, i) => {
          const sev = SEV[item.severity] || SEV.info;
          const clickable = !!item.action;
          return (
            <span
              key={`${item.id}-${i}`}
              onClick={clickable ? () => onAction?.(item.action) : undefined}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: compact ? '0 12px' : '0 16px', height: '100%',
                cursor: clickable ? 'pointer' : 'default',
                fontFamily: MONO,
              }}
              title={clickable ? 'Click to focus' : ''}
            >
              <span style={{
                width: 7, height: 7, borderRadius: '50%',
                background: sev.dot, flexShrink: 0,
                boxShadow: sev.glow !== 'transparent' ? `0 0 6px ${sev.glow}` : 'none',
              }} />
              <span style={{
                color: sev.cat, fontSize: compact ? 9 : 10, fontWeight: 700,
                letterSpacing: '0.12em', opacity: 0.9,
              }}>{item.category}</span>
              <span style={{
                color: 'var(--txt)', fontSize: compact ? 11 : 12, fontWeight: 500,
                letterSpacing: '0.02em',
              }}>{item.text}</span>
              <span style={{
                color: 'var(--hair-2)', fontSize: 14, marginLeft: 8,
              }}>◆</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}